import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';

interface RoutingPanelProps {
  decision: any;
  onClose: () => void;
}

const RoutingPanel: React.FC<RoutingPanelProps> = ({ decision, onClose }) => {
  const getStageIndex = (stage: string) => {
    const stages = ['initial', 'validation', 'review', 'approval', 'submission', 'completed'];
    return stages.indexOf(stage);
  };

  const getActionColor = (action: string): any => {
    switch (action) {
      case 'approve':
        return 'success';
      case 'review':
        return 'info';
      case 'escalate':
        return 'warning';
      case 'reject':
        return 'error';
      case 'forward':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'approve':
        return <CheckIcon />;
      case 'review':
        return <AssignmentIcon />;
      case 'escalate':
        return <TrendingUpIcon />;
      case 'forward':
        return <ArrowForwardIcon />;
      default:
        return <AssignmentIcon />;
    }
  };

  const getPriorityColor = (priority: string): any => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'normal':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatStage = (stage: string) => {
    return stage
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const currentStageIndex = getStageIndex(decision?.current_stage);
  const nextStageIndex = getStageIndex(decision?.next_stage);

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={onClose}
      sx={{
        width: 400,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 400,
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Routing Decision</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {decision ? (
          <>
            {/* Action Summary */}
            <Alert
              severity={getActionColor(decision.action) === 'error' ? 'error' : 'info'}
              icon={getActionIcon(decision.action)}
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle2">
                Recommended Action: {formatStage(decision.action)}
              </Typography>
            </Alert>

            {/* Key Information */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<FlagIcon />}
                  label={`Priority: ${decision.priority}`}
                  color={getPriorityColor(decision.priority)}
                  size="small"
                />
                <Chip
                  icon={<ScheduleIcon />}
                  label={`Est. ${decision.estimated_processing_time} min`}
                  size="small"
                />
                <Chip
                  icon={<TrendingUpIcon />}
                  label={`${Math.round(decision.confidence_score)}% Confidence`}
                  size="small"
                  color="primary"
                />
              </Box>
            </Box>

            {/* Workflow Progress */}
            <Typography variant="subtitle2" gutterBottom>
              Workflow Progress
            </Typography>
            <Stepper activeStep={nextStageIndex} orientation="vertical" sx={{ mb: 3 }}>
              {['Initial', 'Validation', 'Review', 'Approval', 'Submission', 'Completed'].map(
                (label, index) => (
                  <Step key={label} completed={index < currentStageIndex}>
                    <StepLabel
                      StepIconProps={{
                        ...(index === currentStageIndex && { color: 'primary' }),
                        ...(index === nextStageIndex && { color: 'secondary' }),
                      }}
                    >
                      {label}
                      {index === currentStageIndex && (
                        <Chip label="Current" size="small" sx={{ ml: 1 }} />
                      )}
                      {index === nextStageIndex && (
                        <Chip label="Next" size="small" color="primary" sx={{ ml: 1 }} />
                      )}
                    </StepLabel>
                    <StepContent>
                      <Typography variant="caption" color="text.secondary">
                        {index === currentStageIndex && 'Document is currently at this stage'}
                        {index === nextStageIndex && 'Document will move to this stage'}
                      </Typography>
                    </StepContent>
                  </Step>
                )
              )}
            </Stepper>

            <Divider sx={{ my: 2 }} />

            {/* Next Assignee */}
            {decision.next_assignee && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Next Assignee
                </Typography>
                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="action" />
                    <Box>
                      <Typography variant="body2">
                        Role: {formatStage(decision.next_assignee.role)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Level: {formatStage(decision.next_assignee.level)}
                      </Typography>
                      {decision.next_assignee.specialization && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Specialization: {formatStage(decision.next_assignee.specialization)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* Recommendations */}
            {decision.recommendations && decision.recommendations.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Recommendations
                </Typography>
                <List dense>
                  {decision.recommendations.map((rec: string, index: number) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <ArrowForwardIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={rec} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {/* Actions */}
            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
              <Button variant="outlined" fullWidth onClick={onClose}>
                Dismiss
              </Button>
              <Button variant="contained" fullWidth startIcon={getActionIcon(decision.action)}>
                {formatStage(decision.action)}
              </Button>
            </Box>

            {/* Timestamp */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 2, textAlign: 'center' }}
            >
              Decision made: {new Date(decision.timestamp).toLocaleString()}
            </Typography>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Analyzing routing options...
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default RoutingPanel;